import { useMsal } from '@azure/msal-react'
import {
  faArrowLeft,
  faExpand,
  faGlobe,
  faInfoCircle,
  faLocationDot,
  faQrcode,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Divider,
  Group,
  Image,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdMetricsPanel from '../../components/AdMetricsPanel'
import {
  fetchAllLocationsBundleSummary,
  fetchMyAdvertisements,
} from '../../data/advertisement.service'
import { AllLocationsBundleSummary } from '../../models/Dashboard/AllLocationsBundleSummary'
import { DeviceAdvertisementsForHost } from '../../models/DeviceAdvertisementsForHost'
import { useSessionInfo } from '../../contexts/SessionContext'
import { AdStatuses, COLORS, defaultImageUrl, getAdStatusColor, getAdStatusLabel } from '../../utils/constants'

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString()
}

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <Group justify='space-between' gap='md' wrap='nowrap'>
      <Text size='sm' c='dimmed'>
        {label}
      </Text>
      <Text size='sm' ta='right'>
        {value}
      </Text>
    </Group>
  )
}

export default function BundleDetailsPage() {
  const { instance } = useMsal()
  const navigate = useNavigate()
  const { advertisementId: advertisementIdParam } = useParams<{ advertisementId: string }>()
  const { session } = useSessionInfo()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const advertisementId = parseInt(advertisementIdParam ?? '0')

  const [summary, setSummary] = useState<AllLocationsBundleSummary | null>(null)
  const [deviceAds, setDeviceAds] = useState<DeviceAdvertisementsForHost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showImageModal, setShowImageModal] = useState(false)

  useEffect(() => {
    if (!session?.organizationId || !advertisementId) return
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      try {
        const [s, allAds] = await Promise.all([
          fetchAllLocationsBundleSummary(advertisementId, instance),
          fetchMyAdvertisements(session.organizationId, instance),
        ])
        if (cancelled) return
        setSummary(s)
        setDeviceAds(allAds.filter((ad) => ad.advertisementId === advertisementId))
      } catch {
        navigate('/dashboard/myAds')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [advertisementId, instance, session?.organizationId, navigate])

  if (isLoading) {
    return (
      <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
        <Stack p={24} gap={24}>
          <Group>
            <ActionIcon variant='subtle' size='lg' onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </ActionIcon>
            <Skeleton height={32} width={240} />
          </Group>
          <Paper withBorder p='xl' radius='md'>
            <Group align='flex-start' gap='xl'>
              <Skeleton height={175} width={311} radius='md' />
              <Stack style={{ flex: 1 }} gap='sm'>
                <Skeleton height={28} width={260} />
                <Skeleton height={20} width={180} />
                <Skeleton height={20} width={220} />
                <Skeleton height={20} width={150} />
              </Stack>
            </Group>
          </Paper>
        </Stack>
      </div>
    )
  }

  const dateRange = summary?.startDateLocal && summary?.endDateLocal
    ? `${new Date(summary.startDateLocal).toLocaleDateString()} – ${new Date(summary.endDateLocal).toLocaleDateString()}`
    : null

  const approvalParts: string[] = []
  if (summary) {
    if (summary.approvedCount > 0) approvalParts.push(`${summary.approvedCount} approved`)
    if (summary.pendingCount > 0) approvalParts.push(`${summary.pendingCount} pending`)
    if (summary.deniedCount > 0) approvalParts.push(`${summary.deniedCount} denied`)
  }

  return (
    <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
      <Stack p={24} gap={24}>
        <Group>
          <ActionIcon variant='subtle' size='lg' onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </ActionIcon>
          <Title order={2}>Bundle Details</Title>
        </Group>

        {/* Header card */}
        <Paper withBorder p='xl' radius='md'>
          <Group align='flex-start' gap='xl'>
            {/* Image */}
            <div style={{ position: 'relative' }}>
              <AspectRatio ratio={16 / 9} style={{ width: '100%', maxWidth: 350 }}>
                <Image
                  src={summary?.imageUrl || defaultImageUrl}
                  radius='md'
                  style={{ cursor: summary?.imageUrl ? 'pointer' : 'default' }}
                  onClick={() => { if (summary?.imageUrl) setShowImageModal(true) }}
                />
              </AspectRatio>
              {summary?.imageUrl && (
                <ActionIcon
                  variant='filled'
                  radius='md'
                  onClick={() => setShowImageModal(true)}
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    width: 32,
                    height: 32,
                  }}
                >
                  <FontAwesomeIcon icon={faExpand} style={{ fontSize: 14, color: 'white' }} />
                </ActionIcon>
              )}
            </div>

            {/* Details */}
            <Stack style={{ flex: 1 }} gap='md'>
              <Group justify='space-between' align='flex-start'>
                <Title order={2}>{summary?.name ?? 'All Locations Bundle'}</Title>
                <Group gap='sm'>
                  <Badge
                    variant='light'
                    style={{
                      backgroundColor: `${COLORS.LightGreen}22`,
                      color: COLORS.LightGreen,
                    }}
                  >
                    All Locations
                  </Badge>
                  {summary?.autoRenew && (
                    <Badge color='teal' variant='light'>Auto-Renew</Badge>
                  )}
                </Group>
              </Group>

              <Text style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                <FontAwesomeIcon icon={faLocationDot} fixedWidth style={{ color: COLORS.HiddenGemzRed, width: 16 }} />
                {summary?.activeLocationCount ?? 0} active {summary?.activeLocationCount === 1 ? 'location' : 'locations'}
              </Text>

              {dateRange && (
                <Text style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                  <FontAwesomeIcon icon={faInfoCircle} fixedWidth style={{ color: '#666', width: 16 }} />
                  {dateRange}
                </Text>
              )}

              <Text style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                <FontAwesomeIcon icon={faGlobe} fixedWidth style={{ color: COLORS.LightGreen, width: 16 }} />
                {(summary?.totalImpressions ?? 0).toLocaleString()} impressions
              </Text>

              {summary?.qrEnabled && (
                <Text style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                  <FontAwesomeIcon icon={faQrcode} fixedWidth style={{ color: COLORS.LightGreen, width: 16 }} />
                  {(summary.totalQrScans ?? 0).toLocaleString()} QR scans
                </Text>
              )}

              {approvalParts.length > 0 && (
                <Text size='sm' c='dimmed' style={{ paddingLeft: 4 }}>
                  {approvalParts.join(', ')}
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>

        <AdMetricsPanel advertisementId={advertisementId} qrEnabled={summary?.qrEnabled ?? false} />

        {deviceAds.length === 0 ? (
          <Paper withBorder p='xl' radius='md'>
            <Text c='dimmed' ta='center' py='xl'>No device advertisements found for this bundle.</Text>
          </Paper>
        ) : isMobile ? (
          <Stack gap={12}>
            {deviceAds.map((ad) => (
              <Paper key={ad.id} withBorder p='md' radius='md'>
                <Stack gap={8}>
                  <Group justify='space-between' align='flex-start' wrap='nowrap'>
                    <Text fw={600}>{ad.locationName}</Text>
                    <Badge color={getAdStatusColor(ad.status)} size='sm'>
                      {getAdStatusLabel(ad.status)}
                    </Badge>
                  </Group>
                  {ad.status === AdStatuses.Denied && ad.denialReason && (
                    <Text size='xs' c='dimmed'>({ad.denialReason})</Text>
                  )}
                  <Divider />
                  <LabelValue label='Device' value={ad.deviceName || '—'} />
                  <LabelValue label='Start Date' value={formatDate(ad.startDateLocal)} />
                  <LabelValue label='End Date' value={formatDate(ad.endDateLocal)} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Paper withBorder p='xl' radius='md'>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Location</Table.Th>
                  <Table.Th>Device</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Start Date</Table.Th>
                  <Table.Th>End Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deviceAds.map((ad) => (
                  <Table.Tr key={ad.id}>
                    <Table.Td>
                      <Text size='sm' fw={500}>{ad.locationName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>{ad.deviceName || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap='xs'>
                        <Badge color={getAdStatusColor(ad.status)} size='sm'>
                          {getAdStatusLabel(ad.status)}
                        </Badge>
                        {ad.status === AdStatuses.Denied && ad.denialReason && (
                          <Text size='xs' c='dimmed'>({ad.denialReason})</Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>{formatDate(ad.startDateLocal)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>{formatDate(ad.endDateLocal)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      {/* Image expand modal */}
      <Modal
        opened={showImageModal}
        onClose={() => setShowImageModal(false)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: { height: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, backgroundColor: 'black' },
          content: { height: '90vh', maxHeight: '90vh', overflow: 'hidden', backgroundColor: 'black' },
          inner: { padding: 0 },
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'black' }}>
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setShowImageModal(false)}
            style={{ position: 'absolute', top: 16, right: 16, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', zIndex: 1 }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </ActionIcon>
          <div style={{ width: '100%', height: '100%', maxWidth: 'calc(90vh * 16/9)', maxHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Image
              src={summary?.imageUrl}
              alt='Advertisement'
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
